import { SegmentHeader } from "@/components/campaign/basic/SegmentHeader";
import { RadioCard } from "@/components/generic/RadioCard";
import { zodResolver } from "@hookform/resolvers/zod";
import { createFileRoute } from "@tanstack/react-router";
import { CircleCheckBigIcon, UserRoundCheckIcon } from "lucide-react";
import { useEffect, useMemo } from "react";
import { RadioGroup } from "react-aria-components";
import { Controller, useForm } from "react-hook-form";
import z from "zod";

export const Route = createFileRoute(
  "/_dashboard/campaigns/$id/(edit)/delivery/setup/integration/method",
)({
  component: RouteComponent,
  staticData: {
    crumb: "Kvalificering",
    meta: {
      title: "Kvalificering",
    },
  },
});

const exportModes = [
  {
    type: "SalesAutomatic",
    name: "Alle leads",
    description:
      "Leads skal sendes til din integration med det samme, så du kan kvalificere dem dér.",
    icon: UserRoundCheckIcon,
  },
  {
    type: "SalesManual",
    name: "Kvalificerede leads",
    description:
      "Leads skal først sendes til din integration, når de enkeltvist kvalificeres i Lasso Sales.",
    icon: CircleCheckBigIcon,
  },
] as const;

function RouteComponent() {
  const context = Route.useRouteContext();
  const stepper = context.useDeliveryStepper();

  const deliveryContext = stepper.context.delivery;
  if (deliveryContext?.type !== "Integration") throw null;

  const {
    control,
    reset,
    handleSubmit,
    formState: { isValid },
  } = useForm({
    resolver: zodResolver(
      z.object({
        exportMode: z
          .enum(["SalesAutomatic", "SalesManual"])
          .nullable()
          .refine((value) => value !== null, "Vælg en metode"),
      }),
    ),
    defaultValues: {
      exportMode: deliveryContext.exportMode ?? null,
    },
  });

  useEffect(() => {
    reset();
  }, [deliveryContext.exportMode, reset]);

  const submit = useMemo(
    () =>
      handleSubmit((data) => {
        stepper.setContext((prev) => {
          if (prev.delivery?.type !== "Integration") return prev;
          return {
            ...prev,
            delivery: {
              ...prev.delivery,
              exportMode: data.exportMode,
            },
          };
        });

        stepper.goNext();
      }),
    [handleSubmit, stepper],
  );

  context.deliveryStepper.useStepperSubmit(
    useMemo(
      () => ({
        submit,
        isDisabled: !isValid,
      }),
      [submit, isValid],
    ),
  );

  return (
    <form className="flex flex-col gap-y-10" onSubmit={submit}>
      <SegmentHeader
        title="Kvalificering"
        description="Vælg om leads skal kvalificeres, inden de sendes til din integration."
      />

      <Controller
        control={control}
        name="exportMode"
        render={({ field }) => (
          <RadioGroup
            className="flex grow justify-center gap-x-6"
            value={field.value}
            onChange={field.onChange}
            aria-label="Vælg metode"
          >
            {exportModes.map((mode) => (
              <RadioCard
                key={mode.type}
                className="flex size-full max-w-100 min-w-0 flex-col items-center justify-center gap-y-6 rounded-lg p-6 xl:p-12"
                value={mode.type}
                onPress={() => {
                  field.onChange(mode.type);
                  submit();
                }}
                aria-label={mode.name}
              >
                <div className="text-theme-gradient-to bg-theme-gradient-to/30 border-theme-gradient-to/40 flex size-12 items-center justify-center rounded-full border">
                  <mode.icon className="size-6" />
                </div>

                <div className="flex flex-col gap-y-4">
                  <div className="text-center text-base/normal font-semibold">
                    {mode.name}
                  </div>

                  <p className="text-theme-on-background-variant max-w-md text-center text-sm/normal">
                    {mode.description}
                  </p>
                </div>
              </RadioCard>
            ))}
          </RadioGroup>
        )}
      />
    </form>
  );
}
